import type { Profile } from '../../utils/walletSnapshot';

export type AdminLevel = 'admin' | 'superadmin';
export type OrganizerLevel = 'none' | 'pending' | 'full';

const normalize = (value?: string | null): string =>
    String(value ?? '').trim().toLowerCase();

const normalizeKey = (value?: string | null): string =>
    normalize(value).replace(/[^a-z0-9]/g, '');

const FULL_ORGANIZER_LEVELS = new Set(['full', 'active', 'approved']);
const PENDING_ORGANIZER_LEVELS = new Set(['pending', 'review', 'awaiting', 'submitted']);
const NONE_ORGANIZER_LEVELS = new Set(['none', 'inactive', 'disabled', 'rejected']);

export const resolveAdminLevel = (role?: string | null): AdminLevel => {
    return normalizeKey(role).includes('superadmin') ? 'superadmin' : 'admin';
};

export const resolveOrganizerLevel = (profile?: Profile | null): OrganizerLevel => {
    if (!profile) return 'none';

    const normalizedLevel = normalizeKey(profile.organizer_access_level as string | null | undefined);
    if (normalizedLevel) {
        if (FULL_ORGANIZER_LEVELS.has(normalizedLevel)) return 'full';
        if (PENDING_ORGANIZER_LEVELS.has(normalizedLevel)) return 'pending';
        if (NONE_ORGANIZER_LEVELS.has(normalizedLevel)) return 'none';
        if (normalizedLevel.startsWith('full')) return 'full';
        if (normalizedLevel.startsWith('pending') || normalizedLevel.startsWith('review')) return 'pending';
    }

    const role = normalize(profile.role);
    const hasActive = Boolean(profile.has_active_organizer_status);

    if (role === 'organizer') {
        return hasActive ? 'full' : 'pending';
    }

    return 'none';
};

export const canOrganizerManageVenues = (
    profile?: Profile | null,
    organizerLevel?: OrganizerLevel
): boolean => {
    if (!profile) return false;

    const normalizedRole = normalize(profile.role);
    const hasActive = Boolean(profile.has_active_organizer_status);
    const explicitPermission = profile.can_perform_organizer_actions;

    const canPerform =
        typeof explicitPermission === 'boolean'
            ? explicitPermission
            : normalizedRole === 'organizer' && hasActive;

    const resolvedLevel = organizerLevel ?? resolveOrganizerLevel(profile);

    return Boolean(canPerform || resolvedLevel === 'full');
};
